import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Guid } from 'guid-typescript';
import { Observable } from 'rxjs';
import { PathType } from 'src/app/core/enums/path-type.enum';
import {
  Civilite,
  Formule,
  SuperficieChambre,
  SurfaceDependance
} from 'src/app/core/models/form-state.model';
import { TypeRoom } from 'src/app/shared/constants/type-room.constants';
import { getMetropolePackageCode } from 'src/app/shared/helpers/metropole-helper.service';
import { PackageCode } from 'src/app/shared/models/enum/packageCode.enum';
import { ReferenceDataService } from 'src/app/shared/services/reference-data-service';
import { Configuration } from '../configuration';
import { DependencyTypes } from '../models/priceRequest.model';
import { QuotationContractEligibilityResponse } from '../models/quotationContractEligibilityResponse.model';
import { Options, QuotationRequestModel, RiskItem } from '../models/quotationRequest.model';
import { QuotationResponseModel } from '../models/quotationResponse.model';
import { QuotationSummaryResponse } from '../models/quotationSummaryResponse.model';
import { VyVDevisCreateRequest } from '../models/vyVDevisCreateRequest';
import { VyVSharedCodeFormule } from '../models/vyVSharedCodeFormule';
import { VyVSharedProductCode } from '../models/vyVSharedProductCode';
import { VyVTarifCreateRequest } from '../models/vyVTarifCreateRequest';
import { VyVTarifSaisieHabitation } from '../models/vyVTarifSaisieHabitation';
import { DistributorService } from './distributor.service';

@Injectable({
  providedIn: 'root'
})
export class QuotationService {
  public configuration = new Configuration();
  baseUrl: string;
  apiEndpoint: string = 'quotations';

  private static readonly dependencyTypeMap: Record<string, DependencyTypes> = {
    [SurfaceDependance.MoinsDe50m2]: DependencyTypes._0_50m2,
    [VyVTarifSaisieHabitation.SurfaceDependanceEnum.MoinsDe50m2]: DependencyTypes._0_50m2,
    [SurfaceDependance.De50a100m2]: DependencyTypes._50_100m2,
    [VyVTarifSaisieHabitation.SurfaceDependanceEnum.De50a100m2]: DependencyTypes._50_100m2,
    [SurfaceDependance.MoinsDe40m2]: DependencyTypes._0_40m2,
    [VyVTarifSaisieHabitation.SurfaceDependanceEnum.MoinsDe40m2]: DependencyTypes._0_40m2,
    [SurfaceDependance.PlusDe100m2]: DependencyTypes._100_250m2,
    [VyVTarifSaisieHabitation.SurfaceDependanceEnum.PlusDe100m2]: DependencyTypes._100_250m2
  };

  private static readonly productsWithImplicitNoDependency = new Set<VyVSharedProductCode>([
    VyVSharedProductCode.HC,
    VyVSharedProductCode.HM_HC,
    VyVSharedProductCode.HG,
    VyVSharedProductCode.HR,
    VyVSharedProductCode.HA
  ]);

  private static readonly productsWithLargeRooms = new Set<VyVSharedProductCode>([
    VyVSharedProductCode.HC,
    VyVSharedProductCode.HM_HC,
    VyVSharedProductCode.HG,
    VyVSharedProductCode.HR,
    VyVSharedProductCode.HE,
    VyVSharedProductCode.HP,
    VyVSharedProductCode.HL,
    VyVSharedProductCode.HT,
    VyVSharedProductCode.HS,
    VyVSharedProductCode.BS,
    VyVSharedProductCode.HA
  ]);

  private static readonly productsWithStandardPJ = new Set<VyVSharedProductCode>([
    VyVSharedProductCode.HC,
    VyVSharedProductCode.HM_HC
  ]);

  constructor(
    private readonly http: HttpClient,
    configuration: Configuration,
    private readonly refDataService: ReferenceDataService,
    private readonly distributorService: DistributorService
  ) {
    this.baseUrl = configuration.apiBasePath ?? '';
  }

  public CreateQuotation(
    bodytarif: VyVTarifCreateRequest,
    bodyDevis: VyVDevisCreateRequest
  ): Observable<QuotationResponseModel> {
    const priceRequestModel = QuotationService.ToQuotationRequestModel(
      bodytarif,
      bodyDevis,
      this.refDataService,
      this.distributorService
    );
    return this.http.post<QuotationResponseModel>(
      `${this.baseUrl}${this.apiEndpoint}`,
      priceRequestModel
    );
  }

  static ToQuotationRequestModel(
    bodytarif: VyVTarifCreateRequest,
    bodyDevis: VyVDevisCreateRequest,
    refDataService: ReferenceDataService,
    distributorService: DistributorService
  ) {
    // Mapping de VyVTarifCreateRequest vers QuotationRequestModel :
    let riskItems: Array<RiskItem> = [];

    let pathType = bodyDevis.SaisieFormule.IdProduit;
    let address = bodyDevis.SaisieInfosSouscripteur.Adresse;
    address!.NumeroEtRue = address?.NumeroEtRue
      ? address?.NumeroEtRue
      : address?.NumeroDeRue + ' ' + address?.NomDeRue;

    const dependencyTypes = this.getDependencyType(
      bodyDevis.SaisieHabitation.SurfaceDependance,
      bodyDevis.SaisieFormule.IdProduit
    );

    const civilite = this.getCivilityTypeId(bodyDevis.SaisieInfosSouscripteur.Civilite);

    let options: Array<Options> = [];
    if (bodytarif.SaisiePersonnalisation?.AssuranceScolaire?.length ?? 0 > 0) {
      options.push({
        id: 17,
        code: 'RCS',
        metadatas: bodytarif.SaisiePersonnalisation?.AssuranceScolaire?.length.toString()
      });
    }
    if (bodytarif.SaisiePersonnalisation?.ProtectionVelos) {
      options.push({ id: 22, code: 'VELO' });
    }
    if (bodytarif.SaisiePersonnalisation?.ProtectionNomades) {
      options.push({ id: 24, code: 'ANO' });
    }
    if (bodytarif.SaisiePersonnalisation?.ProtectionJuridique) {
      if (this.productsWithStandardPJ.has(bodyDevis.SaisieFormule.IdProduit)) {
        options.push({ id: 16, code: 'PJ' });
      } else {
        options.push({ id: 25, code: 'PJSOL' });
      }
    }
    if (bodytarif.SaisiePersonnalisation?.ValeurANeuf) {
      options.push({
        id: 34,
        code: 'VAN'
      });
    }
    if (bodytarif.SaisiePersonnalisation?.Vol) {
      options.push({
        id: 23,
        code: 'VLX'
      });
    }

    if (
      bodytarif.SaisiePersonnalisation?.MontantValeurObjets2000 === 'Montant10000' ||
      bodytarif.SaisiePersonnalisation?.MontantValeurObjets2000 === 'Montant15000'
    ) {
      const objetsValeurMetadataMap: Record<string, string> = {
        Montant10000: '_10K',
        Montant15000: '_20K'
      };

      options.push({
        id: 13,
        code: 'OBJVAL',
        metadatas:
          objetsValeurMetadataMap[bodytarif.SaisiePersonnalisation?.MontantValeurObjets2000 ?? '']
      });
    }

    let housingRiskItem: RiskItem = {
      Order: 1,
      TypeCode: 'HAB',
      Name: 'Habitation',
      Fields: {
        ADR_COMP: '',
        ADR_RISQUE: address!.NumeroEtRue,
        ADR_CODE_POSTAL: address!.CodePostal,
        ADR_VILLE: address!.Ville,
        HAB_TYPE: bodyDevis.SaisieHabitation.TypeHabitation,
        NATURE_OCCU: bodyDevis.SaisieHabitation.QualiteOccupant,
        NATURE_LOG: bodyDevis.SaisieHabitation.NatureResidence,
        NBR_PIECES: bodyDevis.SaisieHabitation.NombrePieces,
        NBR_COLOC: bodyDevis.SaisieHabitation.NombreColocataires,
        FRANCHISE: '150',
        ANNEE_CONSTRUCTION: 'None'
      }
    };

    housingRiskItem.Fields['REV_FISCAL'] = bodyDevis.SaisieHabitation.MontantPlafondMobilier
      ? bodyDevis.SaisieHabitation.MontantPlafondMobilier
      : 0;

    housingRiskItem.Fields['ADR_ETAGE'] =
      bodyDevis.SaisieHabitation.Etage || VyVTarifSaisieHabitation.EtageEnum.None;

    housingRiskItem.Fields['NOM_BAILLEUR'] = bodyDevis.LessorName ?? '';

    housingRiskItem.Fields['TYPE_CH'] =
      bodyDevis.SaisieHabitation.TypeChambre ?? SuperficieChambre.Undefined;

    if (bodytarif.SaisieHabitation.CompositionFoyer) {
      housingRiskItem.Fields['NBR_PERS'] = bodytarif.SaisieHabitation.CompositionFoyer;
    }

    housingRiskItem.Fields['POSSEDE_VERANDA'] = !!bodytarif.SaisieHabitation.AvecVeranda
      ? VyVTarifSaisieHabitation.VerandaTypeEnum._0_10m2
      : VyVTarifSaisieHabitation.VerandaTypeEnum.None;

    if (
      bodyDevis.SaisieHabitation.NombrePiecesSuperieures !== null &&
      this.productsWithLargeRooms.has(bodyDevis.SaisieFormule.IdProduit)
    ) {
      housingRiskItem.Fields['NBR_PIECES_40M2'] =
        bodyDevis.SaisieHabitation.NombrePiecesSuperieures;
    }

    housingRiskItem.Fields['SURFACE_DEP'] = dependencyTypes
      ? dependencyTypes
      : DependencyTypes._none;

    if (bodyDevis.SaisieHabitation.PiscineSpaJacuzzi !== null) {
      housingRiskItem.Fields['POSSEDE_PISCINE'] = bodyDevis.SaisieHabitation.PiscineSpaJacuzzi;
    }
    if (bodyDevis.SaisieHabitation.ObjetsValeurSuperieur2000 !== null) {
      housingRiskItem.Fields['OBJ_VAL_UNIT'] = bodyDevis.SaisieHabitation.ObjetsValeurSuperieur2000;
    }
    if (bodyDevis.SaisieHabitation.NombreColocataires !== null) {
      housingRiskItem.Fields['NBR_COLOC'] = bodyDevis.SaisieHabitation.NombreColocataires;
    }

    housingRiskItem.Fields['POSSEDE_ENERGIEV'] = bodyDevis.SaisieHabitation.InstallationDD ?? false;
    // Mandatory but not used in this app
    housingRiskItem.Fields['POSSEDE_GARAGE'] = false;
    housingRiskItem.Fields['POSSEDE_CAVE_9M2'] = false;
    housingRiskItem.Fields['TYPE_BAILLEUR'] = 'Unknown';

    if (
      bodyDevis.SaisieHabitation.PlafondMobilier &&
      (bodyDevis.SaisieFormule.IdProduit == VyVSharedProductCode.HC ||
        bodyDevis.SaisieFormule.IdProduit == VyVSharedProductCode.HM_HC)
    ) {
      let nbRooms =
        (bodyDevis.SaisieHabitation?.NombrePieces ?? 0) +
        (bodyDevis.SaisieHabitation?.NombrePiecesSuperieures ?? 0);
      let baremeValeurAssuree = refDataService.baremeValeurAssuree;
      let bareme = baremeValeurAssuree.find(x => x.NombrePieces == nbRooms)!;

      housingRiskItem.Fields['CAP_MOB_BESOIN'] =
        bodyDevis.SaisieHabitation.PlafondMobilier === 'F2' ? bareme.F2 : bareme.F1;
    } else if (
      bodyDevis.SaisieHabitation.PlafondMobilier &&
      pathType !== PathType.HOUSING &&
      pathType !== PathType.HOUSING_ETUDIANTE
    ) {
      const bareme = refDataService
        .getBaremeForMRHMetropoleAltima()
        .find(x => Number(x.pieceNumber) === bodyDevis.SaisieHabitation.NombrePieces!)!;

      let discriminator = bodyDevis.SaisieInfosSouscripteur?.ValeurAssuree ?? undefined;

      if (discriminator) {
        housingRiskItem.Fields['CAP_MOB_BESOIN'] = discriminator + bareme.value;
      }
    } else if (
      bodyDevis.SaisieHabitation.PlafondMobilier &&
      pathType === PathType.HOUSING_ETUDIANTE
    ) {
      const typeChambre = bodyDevis.SaisieHabitation.TypeChambre;
      let critere = '';
      if (
        typeChambre == SuperficieChambre.CROUSMoinsDe10m2 ||
        typeChambre == SuperficieChambre.MoinsDe10m2
      ) {
        critere = 'CHA_U';
      } else if (typeChambre == SuperficieChambre.PlusDe40m2) {
        critere = 'CHA_HAB';
      } else {
        critere = String(
          bodyDevis.SaisieHabitation.NombrePieces! +
            bodyDevis.SaisieHabitation.NombrePiecesSuperieures!
        );
      }

      const bareme = refDataService.getBaremeForMRHEtudiant().find(x => x.pieceNumber === critere)!;

      const discriminator = bodyDevis.SaisieInfosSouscripteur?.ValeurAssuree ?? undefined;

      if (discriminator) {
        housingRiskItem.Fields['CAP_MOB_BESOIN'] = discriminator + bareme.value;
      }
    }
    riskItems.push(housingRiskItem);

    bodytarif.SaisiePersonnalisation?.AssuranceScolaire?.forEach((enfant, index) => {
      let childRiskItem: RiskItem = {
        Order: index + 2,
        TypeCode: 'ENF',
        Name: 'Enfant',
        Fields: {
          NOM_ENFANT: enfant.Nom,
          PRENOM_ENFANT: enfant.Prenom,
          DATE_ENFANT: enfant.DateNaissance
        }
      };
      riskItems.push(childRiskItem);
    });

    if (bodytarif.SaisiePersonnalisation?.ProtectionJuridique) {
      let personRiskItem: RiskItem = {
        Order: riskItems.length + 1,
        TypeCode: 'PER',
        Name: 'Personne',
        Fields: {
          ADR_RISQUE: bodyDevis.SaisieInfosSouscripteur.Adresse?.NumeroEtRue,
          ADR_CODE_POSTAL: bodyDevis.SaisieInfosSouscripteur.Adresse?.CodePostal,
          ADR_VILLE: bodyDevis.SaisieInfosSouscripteur.Adresse?.Ville
        }
      };
      riskItems.push(personRiskItem);
    }
    const quotationRequestModel: QuotationRequestModel = {
      ContextId: bodyDevis.Context,
      PromotionCode: bodytarif.SaisieProduit.CodePromo ?? undefined,
      SponsorEmail: bodytarif.SaisieProduit.SponsorEmail ?? undefined,
      Subscriber: this.toSubscriberModel(bodyDevis.SaisieInfosSouscripteur, civilite, address!),
      Insured: null,
      CoverageDurationInMonths: bodyDevis.CoverageDurationInMonths,
      PackageAndOptionalCoverage: {
        DepartmentCode: address!.CodePostal.substring(0, 2),
        ProductCode: bodyDevis.SaisieFormule.IdProduit,
        HasClaims: bodyDevis.SaisieHabitation.AntecedentSinistresInondables ? true : undefined,
        PackageCode: this.getPackageCode(
          bodyDevis.SaisieFormule.FormuleSelectionnee,
          bodyDevis.SaisieFormule.IdProduit
        ),
        RecommendedPackageCode: bodyDevis.SaisieFormule.IdProduit.includes(VyVSharedProductCode.HC)
          ? this.getPackageCode(
              bodyDevis.SaisieFormule.FormuleRecommandee,
              bodyDevis.SaisieFormule.IdProduit
            )
          : null,
        Options: options,
        DistributorCode: distributorService.GetDistributorCode(),
        LessorName: bodyDevis.LessorName ?? null
      },
      RiskItems: riskItems,
      PartnerCode: bodyDevis.PartnerCode
    };

    if (bodyDevis.SaisieInfoAssure) {
      const civiliteAssure = this.getCivilityTypeId(bodyDevis.SaisieInfoAssure.Civilite);
      quotationRequestModel.Insured = {
        ...this.toSubscriberModel(bodyDevis.SaisieInfoAssure, civiliteAssure, address!)
      };
    }
    return quotationRequestModel;
  }

  private static getDependencyType(
    surfaceDependance: SurfaceDependance | string | null | undefined,
    productCode: VyVSharedProductCode
  ): DependencyTypes | undefined {
    if (
      surfaceDependance === SurfaceDependance.Aucune ||
      surfaceDependance === VyVTarifSaisieHabitation.SurfaceDependanceEnum.Aucune
    ) {
      return DependencyTypes._none;
    }

    if (surfaceDependance && this.dependencyTypeMap[surfaceDependance]) {
      return this.dependencyTypeMap[surfaceDependance];
    }

    if (surfaceDependance === null && this.productsWithImplicitNoDependency.has(productCode)) {
      return DependencyTypes._none;
    }

    return undefined;
  }

  private static getCivilityTypeId(civilite?: string): number {
    if (civilite === Civilite.Monsieur) {
      return 1;
    }

    if (civilite === Civilite.Madame) {
      return 2;
    }

    return 3;
  }

  private static toSubscriberModel(
    person: {
      Nom: string;
      Prenom: string;
      Email: string;
      Telephone?: string | null;
      DateNaissance: string;
      VilleNaissance: string;
      PaysNaissance: string;
      CodePostaleNaissance: string;
      Nationalite: string;
    },
    civilityTypeId: number,
    address: { CodePostal: string; Ville: string }
  ) {
    const [phonePrefix = '+33', cellPhone] = person.Telephone?.split('-') ?? [];

    return {
      CivilityTypeId: civilityTypeId,
      LastName: person.Nom,
      FirstName: person.Prenom,
      Email: person.Email,
      PhonePrefix: phonePrefix,
      CellPhone: cellPhone,
      BirthDate: person.DateNaissance.split('T')[0],
      BirthCity: person.VilleNaissance,
      BirthCountry: person.PaysNaissance,
      BirthZipCode: person.CodePostaleNaissance,
      NationalityCountry: person.Nationalite,
      ZipCode: address.CodePostal,
      City: address.Ville
    };
  }

  static getPackageCode(formule: VyVSharedCodeFormule, idProduit: VyVSharedProductCode): string {
    if (formule === Formule.F1) {
      return PackageCode.CONF;
    } else if (formule === Formule.F2) {
      return PackageCode.CONF_PLUS;
    } else {
      const metropolePackageCode = getMetropolePackageCode(idProduit);
      if (metropolePackageCode) {
        return metropolePackageCode;
      } else {
        return PackageCode.SOLIDAIRE;
      }
    }
  }

  static getRoomType(context: string): string {
    switch (context) {
      case SuperficieChambre.CROUSMoinsDe10m2:
        return TypeRoom.CROUSLessThan10;
      case SuperficieChambre.MoinsDe10m2:
        return TypeRoom.LessThan10;
      case SuperficieChambre.De11a40m2:
        return TypeRoom.Between11And40;
      case SuperficieChambre.PlusDe40m2:
        return TypeRoom.MoreThan40;
      default:
        return '';
    }
  }

  public GetQuotationContractEligibility(
    id: Guid
  ): Observable<QuotationContractEligibilityResponse> {
    return this.http.get<QuotationContractEligibilityResponse>(
      `${this.baseUrl}${this.apiEndpoint}` + '/' + id + '/contractEligibility'
    );
  }
  public GetQuotationSummary(
    reference: string,
    context: Guid,
    email: string
  ): Observable<QuotationSummaryResponse> {
    let filter = new HttpParams();

    if (context) {
      filter = filter.append('contextId', context.toString());
    }

    if (email) {
      filter = filter.append('email', email);
    }

    return this.http.get<QuotationSummaryResponse>(
      `${this.baseUrl}${this.apiEndpoint}/V2/${reference}/summary`,
      {
        params: filter
      }
    );
  }
}
